/**
 * Block 12 PR-B Phase B3 Part C Step 11 — useMethodology hook.
 *
 * Fetches `CalculatorMethodology` per anketa type via apiClient.calculator.getMethodology.
 * 5 P-021 states (ADR-0020 §Q8): idle / loading / success / empty / error.
 * Per-type ref cache — switching anketa types даёт instant render для уже-видели типов.
 *
 * **Paywall (ADR-0019 §Q6):** при 403 SUBSCRIPTION_REQUIRED apiClient throws
 * `ApiClientError({code: 'SUBSCRIPTION_REQUIRED', status: 403})`. Hook surface'ит
 * это через `{status: 'error', error}` — Container (Step 25) инспектирует `error.code`
 * для рендера PaywallModal (ADR-0014). React не catches throws из useEffect — hook
 * вынужден превратить throw в state, Container делает routing.
 *
 * **Cache semantics:** per-Container ref cache (closes при unmount). OQ-3 (ADR-0020):
 * Phase 9+ можно перенести в module-level singleton для cross-Container reuse —
 * сейчас acceptable за simplicity.
 *
 * Reference: ADR-0020 §Q8, ADR-0019 §Q2 (methodology API), §Q6 (paywall).
 */

import { useEffect, useRef, useState } from 'react';
import type { CalculatorAnketaType, CalculatorMethodology } from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';

export type MethodologyState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: CalculatorMethodology }
  | { status: 'empty' }
  | { status: 'error'; error: Error };

/**
 * Type guard для `ApiClientError`-shape без direct import (loose runtime check).
 * Возвращает `true` если err — object с `code: string`. Используется для empty/paywall
 * routing — Container делает дополнительный narrowing по `code`.
 */
function isApiError(err: unknown): err is { code: string; status?: number; message?: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string'
  );
}

/**
 * 404 routing — empty state. Принимает либо явный `code: 'NOT_FOUND'` (Edge contract),
 * либо `status === 404` (defensive против codes like `METHODOLOGY_DISABLED` /
 * `TYPE_NOT_AVAILABLE` если бек когда-то расширит set).
 */
function isNotFound(err: { code: string; status?: number }): boolean {
  return err.code === 'NOT_FOUND' || err.status === 404;
}

export function useMethodology(anketaType: CalculatorAnketaType): MethodologyState {
  const cacheRef = useRef<Map<CalculatorAnketaType, CalculatorMethodology>>(new Map());
  const [state, setState] = useState<MethodologyState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    const cached = cacheRef.current.get(anketaType);
    if (cached) {
      setState({ status: 'success', data: cached });
      return;
    }
    setState({ status: 'loading' });
    apiClient.calculator
      .getMethodology(anketaType)
      .then((data) => {
        if (cancelled) return;
        cacheRef.current.set(anketaType, data);
        setState({ status: 'success', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isApiError(err) && isNotFound(err)) {
          setState({ status: 'empty' });
          return;
        }
        const wrapped = err instanceof Error ? err : new Error(String(err));
        setState({ status: 'error', error: wrapped });
      });
    return () => {
      cancelled = true;
    };
  }, [anketaType]);

  return state;
}
